import React, { useEffect, useState } from "react";
import API from "../api/axios";
import "../styles/dashboard.css";

/* ================= HELPER ================= */
const extractArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    lowStockProducts: 0,
    users: 0,
    orders: 0,
  });

  const [products, setProducts] = useState([]);
  const [modalProducts, setModalProducts] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================= FETCH ================= */
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError("");

      // 🚀 PARALLEL CALLS
      const [usersRes, productsRes, ordersRes] = await Promise.all([
        API.get("/users"),
        API.get("/products"),
        API.get("/orders"),
      ]);

      const usersData = extractArray(usersRes.data, "users");
      const allProducts = extractArray(productsRes.data, "products");
      const ordersData = extractArray(ordersRes.data, "orders");

      // ✅ FIXED TOTAL (IMPORTANT)
      const totalProducts =
        productsRes.data.total || allProducts.length;

      const activeProducts = allProducts.filter(
        (p) => (p.stock || 0) > 0
      ).length;

      const lowStockProducts = allProducts.filter(
        (p) => (p.stock || 0) > 0 && (p.stock || 0) <= 5
      ).length;

      setProducts(allProducts);

      setStats({
        totalProducts,
        activeProducts,
        lowStockProducts,
        users: usersData.length,
        orders: ordersData.length,
      });

    } catch (err) {
      console.error("❌ DASHBOARD ERROR:", err);

      if (err.response?.status === 401) {
        setError("Unauthorized — please login as admin");
      } else {
        setError("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  /* ================= MODAL ================= */
  const openModal = (type) => {
    let filtered = [];

    if (type === "total") {
      filtered = products;
      setModalTitle("All Products");
    } else if (type === "active") {
      filtered = products.filter((p) => (p.stock || 0) > 0);
      setModalTitle("Active Products");
    } else if (type === "lowStock") {
      filtered = products.filter(
        (p) => (p.stock || 0) > 0 && (p.stock || 0) <= 5
      );
      setModalTitle("Low Stock Products");
    }

    setModalProducts(filtered);
    setShowModal(true);
  };

  /* ================= UI STATES ================= */
  if (loading) {
    return (
      <div className="dashboard-container">
        <h3>Loading dashboard...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <h3 style={{ color: "red" }}>{error}</h3>
        <button onClick={fetchStats}>Retry</button>
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="dashboard-container">
      <h2>Dashboard Overview</h2>

      <div className="dashboard-grid">
        <div className="card clickable" onClick={() => openModal("total")}>
          <h3>Total Products</h3>
          <p>{stats.totalProducts}</p>
        </div>

        <div className="card clickable" onClick={() => openModal("active")}>
          <h3>Active Products</h3>
          <p>{stats.activeProducts}</p>
        </div>

        <div
          className="card clickable"
          onClick={() => openModal("lowStock")}
          style={{ color: stats.lowStockProducts > 0 ? "red" : "black" }}
        >
          <h3>Low Stock Products</h3>
          <p>{stats.lowStockProducts}</p>
        </div>

        <div className="card">
          <h3>Total Users</h3>
          <p>{stats.users}</p>
        </div>

        <div className="card">
          <h3>Total Orders</h3>
          <p>{stats.orders}</p>
        </div>
      </div>

      {/* 📊 OPTIONAL INFO */}
      <p style={{ marginTop: "10px", opacity: 0.7 }}>
        Showing {products.length} products from current page
      </p>

      <div className="chart-placeholder">
        <h3>Sales Analytics (Coming Soon)</h3>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{modalTitle}</h3>

            {modalProducts.length === 0 ? (
              <p>No products found.</p>
            ) : (
              <ul>
                {modalProducts.map((p) => (
                  <li key={p._id}>
                    <strong>{p.name}</strong>
                    <br />
                    Stock:{" "}
                    <span style={{ color: p.stock <= 5 ? "red" : "#0f0" }}>
                      {p.stock || 0}
                    </span>
                    {p.isFeatured && " ⭐"}
                  </li>
                ))}
              </ul>
            )}

            <button onClick={() => setShowModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;